import { stopSubmit } from 'redux-form';
import { normalize, schema } from 'normalizr';
import moment from 'moment';
import Config from 'Config';
import globalize, { i18n } from '../services/globalize';
import { refreshToken } from '../../auth/actions';

export const API_ROOT = Config.backofficeApiUrl;

export const getDefaultRequestHeaders = state => ({
    'Content-Type': 'application/json',
    'X-Franchisor-Id': state.configuration.franchisor.id,
    'Accept-Language': globalize.getCurrentCulture(state.configuration.franchisor)
});

const callApi = (apiOptions, schema, dispatch, getState, isRetry) => {
    const state = getState();

    const { method, endpoint, body, authorize } = apiOptions;

    const fullUrl = endpoint.indexOf(API_ROOT) === -1 ? `${API_ROOT}/${endpoint}` : endpoint;

    const options = {
        method,
        headers: getDefaultRequestHeaders(state),
        body
    };

    if (authorize) {
        options.headers['Authorization'] = `Bearer ${state.auth.accessToken}`;
    }

    return fetch(fullUrl, options)
        .then(async response => {
            if (response.headers.get('Content-Type') === 'application/octet-stream') {
                return response.blob().then(blob => {
                    if (!response.ok) {
                        if (response.status === 401 && !isRetry) {
                            // refresh access token and retry current API call
                            return dispatch(refreshToken())
                                .then(() => callApi(apiOptions, schema, dispatch, getState, true))
                                .catch((refreshError) => Promise.reject(refreshError));
                        }
                        else {
                            return Promise.reject(blob);
                        }
                    }
                    return blob;
                });
            }
            else {
                let json;
                try {
                    json = await response.json();
                } catch (jsonError) { }

                if (!response.ok) {
                    if (response.status === 401 && !isRetry) {
                        // refresh access token and retry current API call
                        return dispatch(refreshToken())
                            .then(() => callApi(apiOptions, schema, dispatch, getState, true))
                            .catch((refreshError) => Promise.reject(refreshError));
                    }
                    else {
                        return Promise.reject(json);
                    }
                }
                let result = json;
                if (schema && json) {
                    const { data, totalCount } = json;
                    if (data !== undefined && totalCount !== undefined) {
                        if (totalCount > 0) {
                            result = Object.assign({},
                                normalize(data, schema),
                                { totalCount }
                            );
                        }
                    }
                    else {
                        result = normalize(json, schema);
                    }
                }

                return result;
            }
        });
};

const encodeQueryParams = (params) => {
    let queryString = '';

    if (params) {
        queryString = Object.keys(params)
            .flatMap(k => {
                const paramValue = params[k];
                let values = [paramValue];
                if (Array.isArray(paramValue)) {
                    values = [...paramValue];
                }
                return values.map(value => {
                    if (value instanceof Date) {
                        value = formatDateParam(value);
                    }

                    return (value !== null && value !== undefined && value !== '') ? encodeURIComponent(k) + '=' + encodeURIComponent(value) : null;
                });
            })
            .filter(queryParam => queryParam !== null && queryParam !== undefined)
            .join('&');
    }

    return queryString;
};

const formatDateParam = date => {
    // convert from local time zone to currently selected time zone (configured for moment-timezone) keeping local time
    const momentDate = moment([date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()]);
    return momentDate.format();
};

export const processErrorResponse = (errorResponse) => {
    if (typeof errorResponse === 'string') {
        return {
            message: errorResponse
        };
    }

    const { title, detail, errors } = errorResponse;
    const validationErrors = {};

    if (errors) {
        Object.keys(errors).forEach(key => {
            const dotPosition = key.lastIndexOf('.');
            let propName = key.substring(dotPosition + 1);
            propName = propName.charAt(0).toLowerCase() + propName.slice(1);

            validationErrors[propName] = errors[key][0];
        });
    }
    let newMessage = detail || (title === "One or more validation errors occurred." ? i18n.t('BadRequest_Message') : title);
    if (validationErrors['']) {
        newMessage = validationErrors[''];
        delete validationErrors[''];
    }

    return {
        message: newMessage,
        validationErrors
    };
};

const masterFranchiseeSchema = new schema.Entity('masterFranchisees', {}, {
    idAttribute: masterFranchisee => masterFranchisee.id
});

const franchiseeSchema = new schema.Entity('franchisees', {}, {
    idAttribute: franchisee => franchisee.id
});

const territorySchema = new schema.Entity('territories', {}, {
    idAttribute: territory => territory.id
});

const teacherSchema = new schema.Entity('teachers', {}, {
    idAttribute: teacher => teacher.id
});

const centerSchema = new schema.Entity('centers', {}, {
    idAttribute: center => center.id
});

const termSchema = new schema.Entity('terms', {}, {
    idAttribute: term => term.id
});

const workshopSchema = new schema.Entity('workshops', {}, {
    idAttribute: workshop => workshop.id
});

const weekSessionSchema = new schema.Entity('weekSessions', {}, {
    idAttribute: 'weekId'
});

const childWorkshopSessionSchema = new schema.Entity('childWorkshopSessions', {}, {
    idAttribute: dWorkshopSession => dWorkshopSession.id
});
const workshopSessionSchema = new schema.Entity('workshopSessions', {}, {
    idAttribute: workshopSession => workshopSession.id
});

const roleSchema = new schema.Entity('roles', {}, {
    idAttribute: role => role.id,
    processStrategy: role => ({ ...role, displayName: i18n.t(`SystemUserRole_${role.name}`, role.name) })
});

const userSchema = new schema.Entity('users', {}, {
    idAttribute: user => user.id
});

const noTeacherUserSchema = new schema.Entity('noTeacherUsers', {}, {
    idAttribute: user => user.id
});

const gradeSchema = new schema.Entity('grades', {}, {
    idAttribute: grade => grade.id
});

const documentFormsSchema = new schema.Entity('documentForms', {}, {
    idAttribute: documentForm => documentForm.id
});

const termHolidayPeriodSchema = new schema.Entity('termHolidayPeriods', {}, {
    idAttribute: termHolidayPeriod => termHolidayPeriod.id
});

const franchiseeAllTerritoriesSchema = new schema.Entity('franchiseeAllTerritories', {}, {
    idAttribute: territory => territory.id
});

const priceSchema = new schema.Entity('prices', {}, {
    idAttribute: price => price.id
});

const discountSchema = new schema.Entity('discounts', {}, {
    idAttribute: discount => discount.id
});

const discountUsageSchema = new schema.Entity('discountUsage', {}, {
    idAttribute: usage => usage.id
});

const parentSchema = new schema.Entity('parents', {}, {
    idAttribute: parent => parent.id
});

const childSchema = new schema.Entity('children', {}, {
    idAttribute: child => child.id
});

const parentDocumentSchema = new schema.Entity('parentDocuments', {}, {
    idAttribute: parentDocument => parentDocument.id
});

const sentEmailsSchema = new schema.Entity('sentEmails', {}, {
    idAttribute: sentEmail => sentEmail.communicationId
});

const weeklyProgramSchema = new schema.Entity('weeklyPrograms', {}, {
    idAttribute: weeklyProgram => weeklyProgram.id
});

const permissionSchema = new schema.Entity('permissions', {}, {
    idAttribute: permission => permission.id
});

const trialLessonSchema = new schema.Entity('trialLessons', {}, {
    idAttribute: trialLesson => trialLesson.id
});

const waitingListSchema = new schema.Entity('waitingLists', {}, {
    idAttribute: waitingList => waitingList.id
});

const paymentMethodsByOwnerSchema = new schema.Entity('paymentMethodsByOwner', {}, {
    idAttribute: paymentMethodsByOwner => paymentMethodsByOwner.paymentConfigOwnerId
});

const paymentConfigurationSchema = new schema.Entity('paymentConfigurations', {}, {
    idAttribute: paymentConfiguration => paymentConfiguration.id
});

const voucherSchema = new schema.Entity('vouchers', {}, {
    idAttribute: voucher => voucher.id
});

const paymentHistorySchema = new schema.Entity('paymentHistories', {}, {
    idAttribute: paymentHistory => paymentHistory.id
});

const paymentSchema = new schema.Entity('payments', {}, {
    idAttribute: payment => payment.id
});

const upcomingCancellationsSchema = new schema.Entity('upcomingCancellations', {}, {
    idAttribute: upcomingCancellation => upcomingCancellation.id
});

const instalmentPaymentSchema = new schema.Entity('instalmentPayments', {}, {
    idAttribute: instalmentPayment => instalmentPayment.id
});

const enrolmentSchema = new schema.Entity('enrolments', {}, {
    idAttribute: enrolment => enrolment.id
});

const howYouLearnSchema = new schema.Entity('howYouLearn', {}, {
    idAttribute: howYouLearn => howYouLearn.id
});

const userMappingSchema = new schema.Entity('userMappings', {}, {
    idAttribute: userMapping => userMapping.systemUserId
});

const appSchema = new schema.Entity('apps', {}, {
    idAttribute: app => app.id
});

const parentsNotChooseCenterSchema = new schema.Entity('parentsNotChooseCenter', {}, {
    idAttribute: parentsNotChooseCenter => parentsNotChooseCenter.id
});

const reportChildrenDetailsSchema = new schema.Entity('reportChildrenDetails', {}, {
    idAttribute: reportChildrenDetails => reportChildrenDetails.childId + '.' + reportChildrenDetails.centerId + '.' + reportChildrenDetails.gradeId + '.' + reportChildrenDetails.weekDay + '.' + reportChildrenDetails.startTime
});

const reportChildrenSummarySchema = new schema.Entity('reportChildrenSummary', {}, {
    idAttribute: reportChildrenSummary => reportChildrenSummary.id + '.' + reportChildrenSummary.type
});

const reportChildrenSchema = new schema.Object({ parents: [reportChildrenDetailsSchema], summary: [reportChildrenSummarySchema] });


const messageTemplateSchema = new schema.Entity('messageTemplates', {}, {
    idAttribute: messageTemplate => messageTemplate.id
});

const adHocMessageTemplateSchema = new schema.Entity('adHocMessageTemplates', {}, {
    idAttribute: messageTemplate => messageTemplate.name
});

const childLessonSchema = new schema.Entity('childLessons', {}, {
    idAttribute: childLesson => childLesson.id
});

const makeupSummarySchema = new schema.Entity('makeupSummary', {}, {
    idAttribute: childLesson => childLesson.id
});

const lessonDateSchema = new schema.Entity('lessonDates', {}, {
    idAttribute: 'weeklyProgramItemId'
});

const centerHolidaySchema = new schema.Entity('centerHolidays', {}, {
    idAttribute: centerHoliday => centerHoliday.id
});

const parentsWithPendingPaymentSchema = new schema.Entity('parentsWithPendingPayment', {}, {
    idAttribute: parent => parent.id
});

const weeklyInformationSchema = new schema.Entity('weeklyInformations', {}, {
    idAttribute: weeklyInformation => weeklyInformation.id
});

const localizationSchema = new schema.Entity('localizations', {}, {
    idAttribute: localization => localization.id
});

const franchisorSettingsSchema = new schema.Entity('franchisorSettings', {}, {
    idAttribute: franchisorSettings => franchisorSettings.id
});

const kindergartenPaymentDetailsSchema = new schema.Entity('kindergartenPaymentDetails', {}, {
    idAttribute: 'id'
});

const kindergartenLessonAttendanceSchema = new schema.Object({ kindergartenPaymentDetail: kindergartenPaymentDetailsSchema });

const kinderGartenAttendancesSchema = new schema.Entity('kindergartenAttendances', {
    attendances: [kindergartenLessonAttendanceSchema]
}, {
    idAttribute: kindergartenAttendance => kindergartenAttendance.weeklyProgramItemId + '.' + kindergartenAttendance.month
});

const kindergartenPaymentsSchema = new schema.Entity('kindergartenPayments', {}, {
    idAttribute: kindergartenPayment => kindergartenPayment.id
});

const individualConsultationSchema = new schema.Entity('individualConsultations', {}, {
    idAttribute: individualConsultation => individualConsultation.id
});

const individualConsultationDocumentSchema = new schema.Entity('individualConsultationDocuments', {}, {
    idAttribute: individualConsultationDocument => individualConsultationDocument.id
});

const kindergartenMonthlyReportSchema = new schema.Entity('kindergartenMonthlyReport', {}, {
    idAttribute: k => k.id
});

const monthlyPaymentReportSchema = new schema.Entity('monthlyPaymentReport', {}, {
    idAttribute: monthlyPayment => monthlyPayment.id
});

const prepaidTermsReportSchema = new schema.Entity('prepaidTermsReport', {}, {
    idAttribute: prepaidTerm => prepaidTerm.id
});

const cancellationCreditNotesReportSchema = new schema.Entity('cancellationCreditNotesReport', {}, {
    idAttribute: childLesson => childLesson.id
});

const royaltyReportSchema = new schema.Entity('royaltyReport', {}, {
    idAttribute: royaltyReport => royaltyReport.center.id
});

const centralisedRoyaltyReportSchema = new schema.Entity('centralisedRoyaltyReport', {}, {
    idAttribute: centralisedRoyaltyReport => centralisedRoyaltyReport.center.id
});

const softwareFeeReportSchema = new schema.Entity('softwareFeeReport', {}, {
    idAttribute: softwareFeeReport => `${softwareFeeReport.year}.${softwareFeeReport.month}.${softwareFeeReport.center.id}`
});

const loyalChildrenReportSchema = new schema.Entity('loyalChildrenReport', {}, {
    idAttribute: loyalChildren => loyalChildren.child.id
});

const schoolYearsSchema = new schema.Entity('schoolYears', {}, {
    idAttribute: schoolYear => schoolYear.id
});

const individualConsultationScheduleItemsSchema = new schema.Entity('individualConsultationScheduleItems', {}, {
    idAttribute: individualConsultationScheduleItem => individualConsultationScheduleItem.id
});

const sessionManagementSchema = new schema.Entity('sessionManagement', {}, {
    idAttribute: sessionManagement => sessionManagement.date + '.' + sessionManagement.startTime + '.' + sessionManagement.grade + '.' + sessionManagement.center
});

const emailBounceSchema = new schema.Entity('emailBounces', {}, {
    idAttribute: emailBounce => emailBounce.id
});

const campaignStatsSchema = new schema.Entity('campaignStats', {}, {
    idAttribute: campaignStats => campaignStats.campaignName + '.' + campaignStats.campaignMedium + '.' + campaignStats.campaignSource
});

const campaignStatsDetailsSchema = new schema.Entity('campaignStatsDetails', {}, {
    idAttribute: campaignStatsDetails => campaignStatsDetails.id
});

const individualConsultationFormResponsesSchema = new schema.Entity('individualConsultationFormResponses', {}, {
    idAttribute: individualConsultationFormResponse => individualConsultationFormResponse.id
});

const selfAssessmentSchema = new schema.Entity('selfAssessments', {}, {
    idAttribute: selfAssessment => selfAssessment.id
});

const medicalInformationSchema = new schema.Entity('medicalInformations', {}, {
    idAttribute: medicalInfoFormResponse => medicalInfoFormResponse.childId
});

const noteSchema = new schema.Entity('notes', {}, {
    idAttribute: note => note.id
})

const workshopPaymentSchema = new schema.Entity('workshopPayments', {}, {
    idAttribute: payment => payment.id
})

const authenticationRecordsSchema = new schema.Entity('authenticationRecords', {}, {
    idAttribute: 'spfRecord'
})

const instalmentPlanTemplateSchema = new schema.Entity('instalmentPlanTemplates', {}, {
    idAttribute: instalmentPlanTemplate => instalmentPlanTemplate.id
});

const savedQuerySchema = new schema.Entity('savedQueries', {}, {
    idAttribute: savedQuery => savedQuery.id
});

// Schemas for iClarity API responses.
export const Schemas = {
    MASTER_FRANCHISEE: masterFranchiseeSchema,
    MASTER_FRANCHISEE_ARRAY: [masterFranchiseeSchema],
    FRANCHISEE: franchiseeSchema,
    FRANCHISEE_ARRAY: [franchiseeSchema],
    TERRITORY: territorySchema,
    TERRITORY_ARRAY: [territorySchema],
    CENTER: centerSchema,
    CENTER_ARRAY: [centerSchema],
    TERM: termSchema,
    TERM_ARRAY: [termSchema],
    ROLE: roleSchema,
    ROLE_ARRAY: [roleSchema],
    USER: userSchema,
    USER_ARRAY: [userSchema],
    GRADE: gradeSchema,
    GRADE_ARRAY: [gradeSchema],
    TERM_HOLIDAY_PERIOD: termHolidayPeriodSchema,
    TERM_HOLIDAY_PERIOD_ARRAY: [termHolidayPeriodSchema],
    FRANCHISEE_ALL_TERRITORIES: [franchiseeAllTerritoriesSchema],
    PRICE: priceSchema,
    PRICE_ARRAY: [priceSchema],
    DISCOUNT: discountSchema,
    DISCOUNT_ARRAY: [discountSchema],
    DISCOUNT_USAGE_ARRAY: [discountUsageSchema],
    DOCUMENT_FORM: documentFormsSchema,
    DOCUMENT_FORM_ARRAY: [documentFormsSchema],
    PARENT: parentSchema,
    PARENT_ARRAY: [parentSchema],
    CHILD: childSchema,
    CHILD_ARRAY: [childSchema],
    PARENT_DOCUMENT: parentDocumentSchema,
    PARENT_DOCUMENT_ARRAY: [parentDocumentSchema],
    WEEKLY_PROGRAM: weeklyProgramSchema,
    WEEKLY_PROGRAM_ARRAY: [weeklyProgramSchema],
    PERMISSION: permissionSchema,
    PERMISSION_ARRAY: [permissionSchema],
    TRIAL_LESSON: trialLessonSchema,
    TRIAL_LESSON_ARRAY: [trialLessonSchema],
    WAITING_LIST: waitingListSchema,
    WAITING_LIST_ARRAY: [waitingListSchema],
    PAYMENT_METHODS_BY_OWNER: paymentMethodsByOwnerSchema,
    PAYMENT_CONFIGURATION: [paymentConfigurationSchema],
    VOUCHER: voucherSchema,
    VOUCHER_ARRAY: [voucherSchema],
    PAYMENT_HISTORY: paymentHistorySchema,
    PAYMENT_HISTORY_ARRAY: [paymentHistorySchema],
    INSTALMENT_PAYMENT: instalmentPaymentSchema,
    INSTALMENT_PAYMENT_ARRAY: [instalmentPaymentSchema],
    ENROLMENT: enrolmentSchema,
    ENROLMENTS_ARRAY: [enrolmentSchema],
    HOW_YOU_LEARN: howYouLearnSchema,
    HOW_YOU_LEARN_ARRAY: [howYouLearnSchema],
    USER_MAPPING: userMappingSchema,
    USER_MAPPING_ARRAY: [userMappingSchema],
    APP: appSchema,
    APP_ARRAY: [appSchema],
    PARENTS_NOT_CHOOSE_CENTER_ARRAY: [parentsNotChooseCenterSchema],
    REPORT_CHILDREN_DETAILS_ARRAY: [reportChildrenDetailsSchema],
    REPORT_CHILDREN: reportChildrenSchema,
    MESSAGE_TEMPLATE: messageTemplateSchema,
    MESSAGE_TEMPLATE_ARRAY: [messageTemplateSchema],
    AD_HOC_MESSAGE_TEMPLATE_ARRAY: [adHocMessageTemplateSchema],
    CHILD_LESSON: childLessonSchema,
    CHILD_LESSONS_ARRAY: [childLessonSchema],
    LESSON_DATES: lessonDateSchema,
    CENTER_HOLIDAY: centerHolidaySchema,
    CENTER_HOLIDAYS_ARRAY: [centerHolidaySchema],
    PARENTS_WITH_PENDING_PAYMENT: parentsWithPendingPaymentSchema,
    PARENTS_WITH_PENDING_PAYMENT_ARRAY: [parentsWithPendingPaymentSchema],
    WEEKLY_INFORMATION: weeklyInformationSchema,
    WEEKLY_INFORMATION_ARRAY: [weeklyInformationSchema],
    LOCALIZATION: localizationSchema,
    LOCALIZATION_ARRAY: [localizationSchema],
    FRANCHISOR_SETTINGS: franchisorSettingsSchema,
    KINDERGARTEN_PAYMENT_DETAILS: kindergartenPaymentDetailsSchema,
    KINDERGARTEN_ATTENDANCES: kinderGartenAttendancesSchema,
    KINDERGARTEN_PAYMENT: kindergartenPaymentsSchema,
    KINDERGARTEN_PAYMENT_ARRAY: [kindergartenPaymentsSchema],
    KINDERGARTEN_PAYMENT_DETAIL: kindergartenPaymentDetailsSchema,
    INDIVIDUAL_CONSULTATION: individualConsultationSchema,
    INDIVIDUAL_CONSULTATION_ARRAY: [individualConsultationSchema],
    INDIVIDUAL_CONSULTATION_DOCUMENT: individualConsultationDocumentSchema,
    INDIVIDUAL_CONSULTATION_DOCUMENT_ARRAY: [individualConsultationDocumentSchema],
    KINDERGARTEN_MONTHLY_REPORT_ARRAY: [kindergartenMonthlyReportSchema],
    MONTHLY_PAYMENT_REPORT_ARRAY: [monthlyPaymentReportSchema],
    PREPAID_TERMS_REPORT_ARRAY: [prepaidTermsReportSchema],
    CANCELLATION_CREDIT_NOTES_REPORT_ARRAY: [cancellationCreditNotesReportSchema],
    ROYALTY_REPORT_ARRAY: [royaltyReportSchema],
    CENTRALISED_ROYALTY_REPORT_ARRAY: [centralisedRoyaltyReportSchema],
    SOFTWARE_FEE_REPORT_ARRAY: [softwareFeeReportSchema],
    LOYAL_CHILDREN_REPORT_ARRAY: [loyalChildrenReportSchema],
    SCHOOL_YEARS_ARRAY: [schoolYearsSchema],
    INDIVIDUAL_CONSULTATION_SCHEDULE_ITEM: individualConsultationScheduleItemsSchema,
    INDIVIDUAL_CONSULTATION_SCHEDULE_ITEM_ARRAY: [individualConsultationScheduleItemsSchema],
    SESSION_MANAGEMENT_ARRAY: [sessionManagementSchema],
    TEACHER: teacherSchema,
    TEACHER_ARRAY: [teacherSchema],
    NOTEACHER_ARRAY: [noTeacherUserSchema],
    UPCOMING_CANCELLATIONS_ARRAY: [upcomingCancellationsSchema],
    EMAIL_BOUNCE: emailBounceSchema,
    EMAIL_BOUNCES_ARRAY: [emailBounceSchema],
    CAMPAIGN_STATS_ARRAY: [campaignStatsSchema],
    CAMPAIGN_STATS_DETAILS_ARRAY: [campaignStatsDetailsSchema],
    INDIVIDUAL_CONSULTATION_FORM_RESPONSE: individualConsultationFormResponsesSchema,
    INDIVIDUAL_CONSULTATION_FORM_RESPONSE_ARRAY: [individualConsultationFormResponsesSchema],
    SELF_ASSESSMENT: selfAssessmentSchema,
    SELF_ASSESSMENT_ARRAY: [selfAssessmentSchema],
    MEDICAL_INFORMATION: medicalInformationSchema,
    MEDICAL_INFORMATION_ARRAY: [medicalInformationSchema],
    NOTE: noteSchema,
    NOTE_ARRAY: [noteSchema],
    WORKSHOP: workshopSchema,
    WORKSHOP_ARRAY: [workshopSchema],
    WEEK_SESSIONS: weekSessionSchema,
    CHILD_WORKSHOP_SESSION: childWorkshopSessionSchema,
    CHILD_WORKSHOP_SESSIONS_ARRAY: [childWorkshopSessionSchema],
    WORKSHOP_PAYMENT: workshopPaymentSchema,
    WORKSHOP_PAYMENTS_ARRAY: [workshopPaymentSchema],
    MAKEUP_SUMMARY_ARRAY: [makeupSummarySchema],
    AUTHENTICATION_RECORDS: authenticationRecordsSchema,
    WORKSHOP_SESSIONS_ARRAY: [workshopSessionSchema],
    SENT_EMAILS_ARRAY: [sentEmailsSchema],
    INSTALMENT_PLAN_TEMPLATE: instalmentPlanTemplateSchema,
    INSTALMENT_PLAN_TEMPLATE_ARRAY: [instalmentPlanTemplateSchema],
    PAYMENTS_ARRAY: [paymentSchema],
    SAVED_QUERY: savedQuerySchema,
	SAVED_QUERY_ARRAY: [savedQuerySchema]
};

export const CALL_API = 'Call API';

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
    const callAPI = action[CALL_API];
    if (typeof callAPI === 'undefined') {
        return next(action);
    }

    let { endpoint } = callAPI;
    const { types, method = 'GET', authorize = true, request, schema, form } = callAPI;

    const state = store.getState();

    if (typeof endpoint === 'function') {
        endpoint = endpoint(state);
    }

    if (typeof endpoint !== 'string') {
        throw new Error('Specify a string endpoint URL.');
    }
    if (!Array.isArray(types) || types.length !== 3) {
        throw new Error('Expected an array of three action types.');
    }
    if (!types.every(type => typeof type === 'string')) {
        throw new Error('Expected action types to be strings.');
    }

    const actionWith = data => {
        const finalAction = Object.assign({}, action, data);
        delete finalAction[CALL_API];
        return finalAction;
    };

    const [requestType, successType, failureType] = types;
    next(actionWith({ type: requestType, payload: request }));

    let body = undefined;
    if (method === 'GET') {
        const queryParams = encodeQueryParams(request);
        endpoint = `${endpoint}`;
        if (queryParams) {
            endpoint = `${endpoint}?${encodeQueryParams(request)}`;
        }
    } else {
		body = JSON.stringify(request || {}, (key, value) => {      // prevent error "411 - Content-Length header required" by passing empty object instead of null in the body
            if (key) {
                const initialValue = request[key];
                if (initialValue instanceof Date) {
                    // call format to include timezone info - "2015-09-16T00:00:00+01:00" (moment().toJSON() or default behaviour 
                    // of JSON.stringify returns UTC time)
                    return formatDateParam(initialValue);
                }
            }
            return value;
        });
    }

    const apiOptions = {
        method,
        endpoint,
        body,
        authorize
    };

    return callApi(apiOptions, schema, store.dispatch, store.getState).then(
        response => {
            next(actionWith({
                type: successType,
                replace: action[CALL_API].replace,
                resultIds: action[CALL_API].resultIds,
                response,
                remove: action[CALL_API].remove
            }));

            const { transition } = callAPI;
            if (transition) {
                let { successPath, history, push, isExternal } = transition;
                if (successPath) {
                    if (typeof successPath === 'function') {
                        successPath = successPath(response);
                    }
                    if (typeof successPath !== 'string') {
                        throw new Error('Specify a string redirect URL.');
                    }

                    if (isExternal) {
                        window.location = successPath;
                    } else {
                        if (push) {
                            history.push(successPath);
                        } else {
                            history.replace(successPath);
                        }
                    }
                }
            }
            const { onSuccessEvent } = callAPI;
            if (onSuccessEvent) {
                onSuccessEvent(response);
            }

        },
        errorResponse => {
            const errors = processErrorResponse(errorResponse);

            next(actionWith({
                type: failureType,
                errorResponse
            }));

            if (form) {
                next(stopSubmit(form, { _error: errors.message, ...errors.validationErrors }));
            }
            const { onErrorEvent } = callAPI;
            if (onErrorEvent) {
                onErrorEvent(errors.message);
            }
        }
    );
};